import { ComponentType, ReactNode, useCallback, useMemo } from 'react'

import { DropdownNavList, DropdownNavListProps } from '../../../DropdownNavList'
import { NavItemRenderProps, NavListItemWithBlockDescription } from '../../../NavList'
import { useComboBox } from '../../contexts/comboBoxContext'
import { ComboBoxDisplayValue } from '../../types/comboBoxDisplayValue'
import { ComboBoxValue } from '../../types/comboBoxValue'
import { getItemById } from '../../utils/getItemById'
import { AddNewItemButton } from './elements/AddNewItemButton'
import { ComboBoxDropdownFooterProps, HandleSelectItem } from './types'

export interface ComboBoxDropdownProps
  extends Pick<DropdownNavListProps<ComboBoxValue>, 'placement' | 'selectedId' | 'trigger'> {
  addNewItemButtonText?: ReactNode
  dropdownId?: string
  dropdownMaxHeight?: DropdownNavListProps<ComboBoxValue>['maxHeight']
  dropdownSize?: DropdownNavListProps<ComboBoxValue>['size']
  footer?: ComponentType<ComboBoxDropdownFooterProps>
  shouldDisplayItemValue?: boolean
}

export const ComboBoxDropdown = ({
  addNewItemButtonText,
  dropdownId,
  dropdownMaxHeight,
  dropdownSize,
  footer: Footer,
  selectedId,
  placement,
  trigger,
  shouldDisplayItemValue = true,
}: ComboBoxDropdownProps) => {
  const {
    addItemToSelected,
    addValueToSelected,
    inputValue,
    isDropdownOpen,
    isMaxItemsLimitReached,
    isValueInSelectedItems,
    items,
    setIsDropdownInsideClickedRef,
    setIsDropdownOpen,
  } = useComboBox()

  const handleSelectItem = useCallback<HandleSelectItem>(
    (id: string) => {
      const itemToAdd = getItemById(items, id)

      if (itemToAdd) {
        addItemToSelected(itemToAdd)
      }
      setIsDropdownOpen(false)
    },
    [addItemToSelected, items, setIsDropdownOpen],
  )

  const handleDropdownMouseDown = useCallback(() => {
    setIsDropdownInsideClickedRef(true)
  }, [setIsDropdownInsideClickedRef])

  const handleDropdownItemClick = useCallback(
    (id: string) => {
      setIsDropdownInsideClickedRef(true)
      handleSelectItem(id)
    },
    [handleSelectItem, setIsDropdownInsideClickedRef],
  )

  const handleDropdownClose = useCallback(() => {
    setIsDropdownOpen(false)
  }, [setIsDropdownOpen])

  const handleItemRender = useMemo(
    () =>
      shouldDisplayItemValue
        ? (props: NavItemRenderProps<ComboBoxDisplayValue>) => <NavListItemWithBlockDescription {...props} />
        : undefined,
    [shouldDisplayItemValue],
  )

  const shouldDisplayAddNewItemOption = !!inputValue && !isValueInSelectedItems(inputValue) && !isMaxItemsLimitReached

  const dropdownFooter = useMemo(() => {
    if (Footer) {
      return (
        <Footer
          addItemToSelected={addItemToSelected}
          addValueToSelected={addValueToSelected}
          inputValue={inputValue}
          isMaxItemsLimitReached={isMaxItemsLimitReached}
          isValueInSelectedItems={isValueInSelectedItems}
          items={items}
          onDropdownClose={handleDropdownClose}
          onSelectItem={handleSelectItem}
        />
      )
    }

    if (shouldDisplayAddNewItemOption) {
      return <AddNewItemButton>{addNewItemButtonText}</AddNewItemButton>
    }
  }, [
    Footer,
    shouldDisplayAddNewItemOption,
    addItemToSelected,
    addValueToSelected,
    inputValue,
    isMaxItemsLimitReached,
    isValueInSelectedItems,
    items,
    handleDropdownClose,
    handleSelectItem,
    addNewItemButtonText,
  ])

  return (
    <DropdownNavList
      autoToggle={false}
      footer={dropdownFooter}
      id={dropdownId}
      inputSearchValue={inputValue}
      isOpen={isDropdownOpen}
      items={items}
      itemRender={handleItemRender}
      maxHeight={dropdownMaxHeight}
      onClose={handleDropdownClose}
      onItemClick={handleDropdownItemClick}
      onMouseDown={handleDropdownMouseDown}
      placement={placement}
      selectedId={selectedId}
      shouldSearchForBestMatched
      size={dropdownSize}
      subItemsMode="horizontal"
      trigger={trigger}
      withNavigation
      withNotFound={false}
    />
  )
}
